import React, { useState } from 'react'
import { Box, Center, Text } from '@chakra-ui/react'

import {
    AppointmentFieldsFragment,
    AppointmentStatus,
    useQueryAppointmentsQuery,
} from '~graphql/generated/graphql'
import SharedStack from '~components/shared/wrappers/shared-stack'
import SharedSectionHeader from '~components/shared/texts/shared-section-header'
import SharedSelectableOption from '~components/shared/wrappers/shared-selectable-option'
import LoadingView from '~components/shared/loading-view'
import Pagination from '~components/shared/pagination'
import { getAppointmentStatusColor } from '~components/shared/appointment-status-tag'
import { getIsAppointmentReviewedManually } from '~utils/appointment-helpers'
import { eqBoolFilter, eqStringFilter } from '~utils/query-helpers'
import {
    getAppointmentSelectLabel,
    sortAppointmentsForSelect,
} from './appointment-select-helpers'

interface Props {
    clinicId: string
    /** Scope to a whole client (link modal, which has no patient context). */
    clientId?: string
    /**
     * Scope to one patient (template modal). Takes precedence over clientId
     * when both are given.
     */
    patientId?: string
    /** Used in the empty state when patient-scoped. */
    patientName?: string | null
    selectedAppointment: AppointmentFieldsFragment | undefined
    setSelectedAppointment: (
        appointment: AppointmentFieldsFragment | undefined,
    ) => void
}

const APPOINTMENTS_PER_PAGE = 5

/**
 * Lists appointments as selectable rows (same style as the link/template
 * options) so an inbox user can pick the one a message should use for
 * appointment tags. Upcoming appointments sort first; the "None" row always
 * renders outside the paginated list.
 */
export default function AppointmentSelect({
    clinicId,
    clientId,
    patientId,
    patientName,
    selectedAppointment,
    setSelectedAppointment,
}: Props) {
    const [page, setPage] = useState(0)
    const isPatientScoped = !!patientId

    const { data, loading } = useQueryAppointmentsQuery({
        variables: {
            where: [
                eqStringFilter('clinic', clinicId),
                isPatientScoped
                    ? eqStringFilter('patient', patientId || '')
                    : eqStringFilter('client', clientId || ''),
                eqBoolFilter('is_deleted', false),
                eqStringFilter('status', AppointmentStatus.Cancelled, true),
            ],
            page: 0,
            pageSize: 50,
            orderBy: {
                column: 'start_time',
                isAscending: false,
            },
        },
        skip: !clinicId || (!clientId && !patientId),
    })

    const appointments = sortAppointmentsForSelect(
        data?.queryAppointments ?? [],
    )
    const pageCount = Math.max(
        1,
        Math.ceil(appointments.length / APPOINTMENTS_PER_PAGE),
    )
    // Clamp during render (no effect needed) so a shrinking result set can
    // never leave the list stranded on an empty page.
    const safePage = Math.min(page, pageCount - 1)
    const visibleAppointments = appointments.slice(
        safePage * APPOINTMENTS_PER_PAGE,
        safePage * APPOINTMENTS_PER_PAGE + APPOINTMENTS_PER_PAGE,
    )

    function handleSelectAppointment(
        appointment: AppointmentFieldsFragment | undefined,
    ) {
        setSelectedAppointment(
            appointment?.id === selectedAppointment?.id
                ? undefined
                : appointment,
        )
    }

    return (
        <SharedStack gap={2}>
            <SharedSectionHeader>Select an appointment</SharedSectionHeader>
            {loading ? (
                <LoadingView py={4} />
            ) : (
                <>
                    <SharedSelectableOption
                        label="None"
                        isSelected={!selectedAppointment}
                        onClick={() => handleSelectAppointment(undefined)}
                    />
                    {visibleAppointments.map(appointment => {
                        const isActive =
                            appointment.id === selectedAppointment?.id
                        const statusColor = getAppointmentStatusColor(
                            appointment.status,
                            getIsAppointmentReviewedManually(appointment),
                        )

                        return (
                            <SharedSelectableOption
                                key={appointment.id}
                                label={getAppointmentSelectLabel(appointment)}
                                isSelected={isActive}
                                onClick={() =>
                                    handleSelectAppointment(appointment)
                                }
                                leftFlair={
                                    <Box
                                        mr={4}
                                        boxSize="10px"
                                        flexShrink={0}
                                        borderRadius="full"
                                        bg={statusColor}
                                    />
                                }
                            />
                        )
                    })}
                    {!appointments.length && (
                        <Text fontSize="sm" color="gray.500">
                            {isPatientScoped && patientName
                                ? `No appointments found for ${patientName}.`
                                : 'No appointments found for this client.'}
                        </Text>
                    )}
                    {appointments.length > APPOINTMENTS_PER_PAGE && (
                        <Center w="full">
                            <Pagination
                                page={safePage}
                                setPage={setPage}
                                pageSize={APPOINTMENTS_PER_PAGE}
                                count={appointments.length}
                            />
                        </Center>
                    )}
                </>
            )}
        </SharedStack>
    )
}
